import React from 'react';
import { X, User, Calendar, CreditCard, Key, Database, Building, Users, HardDrive, Zap, MessageSquare, MessagesSquare, Layers } from 'lucide-react';
import Button from '../ui/Button';
import { TenantRecord } from '../../types';

interface TenantDetailsModalProps {
  tenant: TenantRecord;
  onClose: () => void;
  onImpersonate: (tenantId: string) => void;
  onDelete: (tenant: TenantRecord) => void;
}

const TenantDetailsModal: React.FC<TenantDetailsModalProps> = ({
  tenant,
  onClose,
  onImpersonate,
  onDelete,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'trialing':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'canceled':
      case 'cancelled':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getPlanColor = (plan: string | undefined) => {
    switch (plan?.toLowerCase()) {
      case 'enterprise':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'pro':
      case 'professional':
        return 'bg-primary/10 text-primary dark:bg-primary/20';
      case 'starter':
        return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm h-screen">
      <div className="bg-white dark:bg-surface w-full max-w-3xl rounded-xl shadow-2xl flex flex-col max-h-[90vh] border dark:border-border-color border-light-border-color animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="p-6 border-b dark:border-border-color border-light-border-color flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-primary/10 rounded-xl">
                <Building className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold dark:text-white text-gray-900">
                  {tenant.name}
                </h2>
                <p className="text-sm dark:text-text-secondary text-gray-500 font-mono">
                  {tenant.tenantId}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-primary/5 dark:bg-primary/10 p-4 rounded-xl border border-primary/10">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-primary" />
                <p className="text-xs font-medium text-primary uppercase tracking-wide">Users</p>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{tenant.userCount || 0}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">API Keys</p>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{tenant.apiKeyCount || 0}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <HardDrive className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Storage</p>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {tenant.storageUsed ? `${(tenant.storageUsed / 1024 / 1024).toFixed(1)}` : '0'}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">MB</span>
              </p>
            </div>
          </div>

          {/* Subscription & Plan Section */}
          <div className="bg-white dark:bg-gray-800/30 rounded-xl border dark:border-border-color border-light-border-color overflow-hidden">
            <div className="px-5 py-4 border-b dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/50">
              <h3 className="font-semibold dark:text-white text-gray-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-primary" />
                Subscription & Plan
              </h3>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Current Plan Card */}
                <div className={`p-4 rounded-xl border-2 ${
                  tenant.subscriptionPlan?.toLowerCase() === 'enterprise' 
                    ? 'bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-800/10 border-purple-200 dark:border-purple-700/50'
                    : tenant.subscriptionPlan?.toLowerCase() === 'pro' || tenant.subscriptionPlan?.toLowerCase() === 'professional'
                    ? 'bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 border-primary/30'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-800/30 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">Current Plan</span>
                  </div>
                  <p className={`text-lg font-bold capitalize ${
                    tenant.subscriptionPlan?.toLowerCase() === 'enterprise'
                      ? 'text-purple-700 dark:text-purple-300'
                      : tenant.subscriptionPlan?.toLowerCase() === 'pro' || tenant.subscriptionPlan?.toLowerCase() === 'professional'
                      ? 'text-primary'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {tenant.subscriptionPlan || 'N/A'}
                  </p>
                </div>
                
                {/* Status Card */}
                <div className={`p-4 rounded-xl border-2 ${
                  tenant.subscriptionStatus?.toLowerCase() === 'active'
                    ? 'bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 border-green-200 dark:border-green-700/50'
                    : tenant.subscriptionStatus?.toLowerCase() === 'trialing'
                    ? 'bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 border-blue-200 dark:border-blue-700/50'
                    : tenant.subscriptionStatus?.toLowerCase() === 'past_due'
                    ? 'bg-gradient-to-br from-yellow-50 to-yellow-100/50 dark:from-yellow-900/20 dark:to-yellow-800/10 border-yellow-200 dark:border-yellow-700/50'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-800/30 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">Status</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      tenant.subscriptionStatus?.toLowerCase() === 'active' ? 'bg-green-500 animate-pulse' :
                      tenant.subscriptionStatus?.toLowerCase() === 'trialing' ? 'bg-blue-500 animate-pulse' :
                      tenant.subscriptionStatus?.toLowerCase() === 'past_due' ? 'bg-yellow-500' :
                      'bg-gray-400'
                    }`} />
                    <p className={`text-lg font-bold capitalize ${
                      tenant.subscriptionStatus?.toLowerCase() === 'active'
                        ? 'text-green-700 dark:text-green-300'
                        : tenant.subscriptionStatus?.toLowerCase() === 'trialing'
                        ? 'text-blue-700 dark:text-blue-300'
                        : tenant.subscriptionStatus?.toLowerCase() === 'past_due'
                        ? 'text-yellow-700 dark:text-yellow-300'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {tenant.subscriptionStatus || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {tenant.stripeCustomerId && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                    Stripe Customer ID
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 font-mono mt-2 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg border dark:border-gray-700 border-gray-200">
                    {tenant.stripeCustomerId}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* API Usage Section */}
          {tenant.currentUsage && (
            <div className="bg-white dark:bg-gray-800/30 rounded-xl border dark:border-border-color border-light-border-color overflow-hidden">
              <div className="px-5 py-4 border-b dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/50">
                <h3 className="font-semibold dark:text-white text-gray-900 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  Current Cycle Usage
                </h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <div className="flex justify-center mb-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold dark:text-white text-gray-900">
                      {tenant.currentUsage.apiCalls?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide">API Calls</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <div className="flex justify-center mb-2">
                      <MessagesSquare className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold dark:text-white text-gray-900">
                      {tenant.currentUsage.conversations?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide">Conversations</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <div className="flex justify-center mb-2">
                      <MessageSquare className="w-5 h-5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold dark:text-white text-gray-900">
                      {tenant.currentUsage.messages?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide">Messages</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* API Keys Section */}
          <div className="bg-white dark:bg-gray-800/30 rounded-xl border dark:border-border-color border-light-border-color overflow-hidden">
            <div className="px-5 py-4 border-b dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/50">
              <h3 className="font-semibold dark:text-white text-gray-900 flex items-center gap-2">
                <Key className="w-4 h-4 text-primary" />
                API Keys
              </h3>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b dark:border-border-color border-light-border-color">
                  <span className="dark:text-text-secondary text-gray-600">SDK</span>
                  <span className="font-semibold dark:text-white text-gray-900">{tenant.apiKeysByType?.sdk || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b dark:border-border-color border-light-border-color">
                  <span className="dark:text-text-secondary text-gray-600">Integration</span>
                  <span className="font-semibold dark:text-white text-gray-900">{tenant.apiKeysByType?.integration || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b dark:border-border-color border-light-border-color">
                  <span className="dark:text-text-secondary text-gray-600">Internal</span>
                  <span className="font-semibold dark:text-white text-gray-900">{tenant.apiKeysByType?.internal || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 pt-3 border-t-2 dark:border-primary/30 border-primary/30">
                  <span className="font-semibold dark:text-white text-gray-900">Total</span>
                  <span className="font-bold text-primary text-lg">{tenant.apiKeyCount || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Database Section */}
          <div className="bg-white dark:bg-gray-800/30 rounded-xl border dark:border-border-color border-light-border-color overflow-hidden">
            <div className="px-5 py-4 border-b dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/50">
              <h3 className="font-semibold dark:text-white text-gray-900 flex items-center gap-2">
                <Database className="w-4 h-4 text-primary" />
                Database Information
              </h3>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                    Database Name
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 font-mono mt-2 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg border dark:border-gray-700 border-gray-200">
                    tenant_{tenant.tenantId}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                    Storage Used
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 mt-2">
                    {tenant.storageUsed ? `${(tenant.storageUsed / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Timestamps Section */}
          <div className="bg-white dark:bg-gray-800/30 rounded-xl border dark:border-border-color border-light-border-color overflow-hidden">
            <div className="px-5 py-4 border-b dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/50">
              <h3 className="font-semibold dark:text-white text-gray-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Timestamps
              </h3>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                    Created At
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 mt-2">
                    {formatDate(tenant.createdAt)}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                    Last Updated
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 mt-2">
                    {formatDate(tenant.updatedAt)}
                  </p>
                </div>
                {tenant.lastActivity && (
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                      Last Activity
                    </label>
                    <p className="text-sm dark:text-white text-gray-900 mt-2">
                      {formatDate(tenant.lastActivity)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Plan Overrides (if any) */}
          {tenant.planOverrides && Object.keys(tenant.planOverrides).length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 overflow-hidden">
              <div className="px-5 py-4 border-b border-blue-200 dark:border-blue-800 bg-blue-100/50 dark:bg-blue-900/30">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Plan Overrides
                </h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {Object.entries(tenant.planOverrides).map(([key, value]) => (
                    <div key={key}>
                      <label className="text-xs text-blue-600 dark:text-blue-400 uppercase tracking-wide font-medium">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <p className="text-sm dark:text-white text-gray-900 mt-2 font-medium">
                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/30">
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} className="flex-1">
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => onImpersonate(tenant.tenantId)}
              className="flex-1"
            >
              Impersonate
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                onClose();
                onDelete(tenant);
              }}
              className="flex-1"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDetailsModal;
